/**
 * 料理の「形」を物理ボディと表示サイズへ変換する。
 *
 * 実画像に輪郭（凸多角形 hull）が付いている場合は、その形のボディを作る。
 * 付いていない場合（仮アイコンなど）は従来どおり半径 radius の円。
 *
 * 大きさの基準:
 *   多角形ボディの面積が「半径 radius の円」と同じになるように拡大率 K を決める。
 *   こうすると形が変わってもレベルごとの大きさ・重さのバランスが崩れない。
 *
 * hull の座標系:
 *   正方形の画像全体を -1..1 に正規化したもの（左上が -1,-1 / 右下が 1,1）。
 *   scripts/process-food-images.mjs が画像の不透明部分から自動生成する。
 */

import type { ItemHull, StageItemDefinition } from '../config/types';

export interface Vec2 {
  x: number;
  y: number;
}

export interface CircleShape {
  kind: 'circle';
  /** 盤面での実効半径（縮小倍率を掛けたあと。面積は π × radius²）。 */
  radius: number;
  /** 表示サイズ（正方形の一辺）。 */
  displaySize: number;
  originX: number;
  originY: number;
  /** ボディの面積（質量計算用）。 */
  area: number;
  /** 中心からの左右の張り出し（器の壁との距離計算用）。 */
  halfWidth: number;
}

export interface PolygonShape {
  kind: 'polygon';
  /** 盤面での実効半径（縮小倍率を掛けたあと。面積は π × radius²）。 */
  radius: number;
  displaySize: number;
  /** テクスチャ上の重心位置（0..1）。Matter はボディ位置を重心に置くため、画像の原点をそこに合わせる。 */
  originX: number;
  originY: number;
  area: number;
  halfWidth: number;
  /** 重心を原点としたワールド座標の頂点。 */
  vertices: Vec2[];
}

export type ResolvedShape = CircleShape | PolygonShape;

/** 符号付き面積（時計回り・反時計回りどちらでも絶対値を使う）。 */
export function polygonArea(points: ItemHull): number {
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

/** 多角形の重心（面積重心）。 */
export function polygonCentroid(points: ItemHull): Vec2 {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    const cross = x1 * y2 - x2 * y1;
    a += cross;
    cx += (x1 + x2) * cross;
    cy += (y1 + y2) * cross;
  }
  if (Math.abs(a) < 1e-9) {
    const n = points.length || 1;
    return {
      x: points.reduce((s, p) => s + p[0], 0) / n,
      y: points.reduce((s, p) => s + p[1], 0) / n,
    };
  }
  return { x: cx / (3 * a), y: cy / (3 * a) };
}

/** hull が物理ボディとして使えるか（3頂点以上・面積あり・範囲内）。 */
export function isUsableHull(hull: ItemHull | undefined): hull is ItemHull {
  if (!hull || hull.length < 3) return false;
  if (hull.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) return false;
  if (hull.some(([x, y]) => Math.abs(x) > 1.05 || Math.abs(y) > 1.05)) return false;
  return polygonArea(hull) > 0.05;
}

/**
 * アイテムの形を解決する。
 * @param useImageShape 実画像を表示するときだけ true（仮アイコンは円のまま）
 */
export function resolveItemShape(
  item: Pick<StageItemDefinition, 'radius' | 'hull'>,
  useImageShape: boolean,
  /**
   * 盤面での縮小倍率（config/shapeScales.ts）。輪郭のある実画像にだけ掛ける。
   * 横長で1つ上の段より幅が広くなってしまう料理を、当たり判定と絵の両方で同じだけ縮める。
   * 質量は item.mass のまま（density = mass / area で決まるため）。
   */
  scale = 1,
): ResolvedShape {
  if (!useImageShape || !isUsableHull(item.hull)) {
    const r = item.radius;
    return {
      kind: 'circle',
      radius: r,
      displaySize: r * 2,
      originX: 0.5,
      originY: 0.5,
      area: Math.PI * r * r,
      halfWidth: r,
    };
  }

  const r = item.radius * (scale > 0 && scale <= 1 ? scale : 1);
  const hull = item.hull;
  // 正規化座標 1 あたりのワールド長さ（面積を円と揃える）
  const k = Math.sqrt(Math.PI / polygonArea(hull)) * r;
  const c = polygonCentroid(hull);
  const vertices = hull.map(([x, y]) => ({ x: (x - c.x) * k, y: (y - c.y) * k }));

  return {
    kind: 'polygon',
    radius: r,
    displaySize: 2 * k,
    originX: (c.x + 1) / 2,
    originY: (c.y + 1) / 2,
    area: Math.PI * r * r,
    halfWidth: Math.max(...vertices.map((v) => Math.abs(v.x))),
    vertices,
  };
}

/** 画像ファイル名（拡張子なし）をキーにした輪郭データを各アイテムへ付与する。 */
export function attachHulls<T extends StageItemDefinition>(
  items: readonly T[],
  hulls: Readonly<Record<string, ItemHull>>,
): T[] {
  return items.map((item) => {
    if (!item.image) return item;
    const base =
      item.image
        .split('/')
        .pop()
        ?.replace(/\.[a-z0-9]+$/i, '') ?? '';
    const hull = hulls[base];
    return hull ? { ...item, hull } : item;
  });
}

/**
 * 盤面での横幅（輪郭の x 方向の幅、傾き 0 のとき）。
 * 輪郭が無い・使えない料理は円（2 × radius）として測る。
 */
export function boardWidthOf(
  item: Pick<StageItemDefinition, 'radius' | 'hull'>,
  scale = 1,
): number {
  return boardExtentOf(item, scale).width;
}

/** 盤面での横幅と高さ（傾き 0 のとき）。 */
export function boardExtentOf(
  item: Pick<StageItemDefinition, 'radius' | 'hull'>,
  scale = 1,
): { width: number; height: number } {
  const shape = resolveItemShape(item, true, scale);
  if (shape.kind === 'circle') return { width: shape.displaySize, height: shape.displaySize };
  const xs = shape.vertices.map((v) => v.x);
  const ys = shape.vertices.map((v) => v.y);
  return { width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
}

/**
 * 縦長の品（高さが横幅の 1.3 倍以上。でこまわし・ソフトクリームなど）。
 * 横幅は小さくても背が高く、上の段だと見て分かるので、下の段の横幅の「上限」には使わない。
 * （使うと、下の段を軒並み半分近くまで縮めることになる）
 */
export const TALL_SHAPE_RATIO = 1.3;

export function isTallShape(item: Pick<StageItemDefinition, 'radius' | 'hull'>): boolean {
  const { width, height } = boardExtentOf(item);
  return height >= width * TALL_SHAPE_RATIO;
}

/**
 * 「下の段ほど横幅が小さい」を保つための縮小倍率を求める。
 *
 * 上の段から順に見て、その段の横幅が「それより上のどの段の横幅 × ratio」を超えるときだけ、
 * ちょうど収まる倍率（小数2桁へ切り捨て）を付ける。上の段は縮めない（必要最小限）。
 * 縦長の品（isTallShape）は比べる相手にしない。
 * 返り値は倍率が 1 未満の料理だけ（料理ID → 倍率）。
 */
export function computeShapeScales(
  items: ReadonlyArray<Pick<StageItemDefinition, 'id' | 'level' | 'radius' | 'hull'>>,
  ratio = 0.97,
  minScale = 0,
  skipAbove = 1,
  minOverrides: Readonly<Record<string, number>> = {},
): Record<string, number> {
  const sorted = [...items].sort((a, b) => b.level - a.level);
  const scales: Record<string, number> = {};
  let cap = Infinity; // ここより上の段の横幅の最小値
  for (const item of sorted) {
    const width = boardWidthOf(item);
    let scale = 1;
    if (Number.isFinite(cap) && width > cap * ratio) {
      const need = Math.floor(((cap * ratio) / width) * 100) / 100;
      // ほんの少し広いだけなら縮めない。縮めても minScale より小さくはしない
      if (need < skipAbove) {
        scale = Math.max(need, minOverrides[item.id] ?? minScale);
        scales[item.id] = scale;
      }
    }
    if (!isTallShape(item)) cap = Math.min(cap, width * scale);
  }
  return scales;
}
